import { type } from 'arktype';
import type { SlotEligibilityBreakdown, SlotEligibilityReason } from '../utils/slot-eligibility.utils';
import type { EffectiveSkillRatings } from '../utils/skill-matching.utils';
import type { ConflictType } from '../validation/conflict.types';

/**
 * Options controlling solver behavior.
 */
export interface SolverOptions {
  /** Maximize shift coverage */
  maxCoverage: boolean;
  /** Balance workload fairly across employees */
  fairness: boolean;
  /** Prefer employees with matching skills */
  skillBalance: boolean;
  /** Prefer employees with lower cumulative hour balances (soft constraint) */
  balancePreference: boolean;
  /**
   * When true, the solver stops much sooner after reaching a feasible
   * solution instead of continuing to fine-tune soft-constraint quality —
   * same constraints apply, just less time spent optimising them. Trades
   * solution quality for speed; all other options are unaffected.
   */
  fast: boolean;
}

/**
 * Numeric weights per soft constraint. Higher = the solver works harder to
 * minimise that penalty. `0` disables the constraint. Replaces the coarse
 * boolean toggles in {@link SolverOptions}; the booleans remain available
 * for backwards compatibility — `false` maps to weight `0`, `true` to the
 * default for that constraint.
 *
 * Hard constraints (allowedCandidate, incompatibleSlotOverlap, maxCoverage)
 * cannot be weight-tuned: they are inviolable. Only the named soft tier is
 * exposed here.
 *
 * Per ADR (SOLV-5): planners can override these per tenant via
 * `strategies.data.solverWeights`. When neither the request nor the strategy
 * override provides weights, {@link DEFAULT_SOLVER_WEIGHTS} is used.
 */
export interface SolverWeights {
  /** Soft: prefer employees with lower cumulative hour balances. Default 10. */
  balancePreference: number;
  /** Soft: deprioritise floater (lent-from-other-team) employees. Default 10. */
  floaterPenalty: number;
  /** Soft: prefer employees whose skill count matches the shift requirement (tiebreaker). Default 1. */
  skillMatchQuality: number;
  /** Soft: spread hours evenly across employees. Default 1. */
  fairness: number;
}

export const DEFAULT_SOLVER_WEIGHTS: SolverWeights = {
  balancePreference: 10,
  floaterPenalty: 10,
  skillMatchQuality: 1,
  fairness: 1,
};

export const SOLVER_PROGRESS_RULE_NAMES = [
  'allowedCandidate',
  'incompatibleSlotOverlap',
  'weeklyHoursCap',
  'maxCoverage',
  'preferLowBalance',
  'floaterPenalty',
  'skillMatchQuality',
  'fairness',
] as const;

export type SolverProgressRuleName = (typeof SOLVER_PROGRESS_RULE_NAMES)[number];
export type SolverProgressRuleState = 'pending' | 'active' | 'done';

/**
 * Two-tier classification of every progress rule, consumed by the progress
 * dialog to render rules under "Eligibility" vs "Preferences" subheaders
 * (AC13 in docs/specs/auto-fill-solver-correctness.md).
 *
 * `eligibility` = inviolable filter — the solver is never allowed to violate
 * these (allowed-candidate set, non-overlapping shifts, maxCoverage's
 * medium-tier fill incentive that the settings dialog exposes as a toggle).
 *
 * `preferences` = optimisation objectives active only in this run —
 * disabling them cannot make coverage worse but shapes who is picked.
 *
 * A spec test asserts every entry in {@link SOLVER_PROGRESS_RULE_NAMES}
 * has a classification here; adding a new rule name without a tier fails
 * the build.
 */
export const RULE_TIER: Record<SolverProgressRuleName, 'eligibility' | 'preferences'> = {
  allowedCandidate: 'eligibility',
  incompatibleSlotOverlap: 'eligibility',
  weeklyHoursCap: 'eligibility',
  maxCoverage: 'eligibility',
  preferLowBalance: 'preferences',
  floaterPenalty: 'preferences',
  skillMatchQuality: 'preferences',
  fairness: 'preferences',
};

export interface SolverProgressRule {
  name: SolverProgressRuleName;
  state: SolverProgressRuleState;
}

/**
 * Resolve rules that participate in this solve. Hard constraints are always
 * active; soft constraints follow both legacy option gates and configured
 * weights. This list is metadata for progress UI, not solver configuration.
 */
export function getActiveSolverProgressRuleNames(
  options: Partial<SolverOptions> = {},
  weights: SolverWeights = DEFAULT_SOLVER_WEIGHTS
): SolverProgressRuleName[] {
  const opts = options ?? {};
  const rules: SolverProgressRuleName[] = ['allowedCandidate', 'incompatibleSlotOverlap', 'weeklyHoursCap'];

  if (opts.maxCoverage ?? true) rules.push('maxCoverage');
  if ((opts.balancePreference ?? true) && (weights?.balancePreference ?? 0) > 0) rules.push('preferLowBalance');
  if ((weights?.floaterPenalty ?? 0) > 0) rules.push('floaterPenalty');
  if ((opts.skillBalance ?? true) && (weights?.skillMatchQuality ?? 0) > 0) rules.push('skillMatchQuality');
  if ((opts.fairness ?? true) && (weights?.fairness ?? 0) > 0) rules.push('fairness');

  return rules;
}

/**
 * Convert legacy boolean {@link SolverOptions} into the weighted form.
 * - `true` maps to the constraint's default weight.
 * - `false` maps to `0` (disabled).
 */
export function solverOptionsToWeights(options: SolverOptions): SolverWeights {
  return {
    balancePreference: options.balancePreference ? DEFAULT_SOLVER_WEIGHTS.balancePreference : 0,
    floaterPenalty: DEFAULT_SOLVER_WEIGHTS.floaterPenalty,
    skillMatchQuality: options.skillBalance ? DEFAULT_SOLVER_WEIGHTS.skillMatchQuality : 0,
    fairness: options.fairness ? DEFAULT_SOLVER_WEIGHTS.fairness : 0,
  };
}

/**
 * A single diagnostic code returned by the solver.
 */
export interface DiagnosticCode {
  code: string;
  metrics: Record<string, number>;
}

/**
 * Ordered phases the solver job reports via `job.updateProgress`. Each phase
 * carries the percentage the processor emits when it enters that phase, so the
 * UI can render a live stepper (a step is "done" once the reported percent has
 * passed it, "active" at the current step). Translation keys live under
 * `plans.solver.steps.<step>`.
 */
export type SolverProgressStep =
  | 'LOAD_VERSION'
  | 'COMPUTE_BLANKS'
  | 'LOAD_DATA'
  | 'CHECK_ELIGIBILITY'
  | 'BUILD_REQUEST'
  | 'SOLVE'
  | 'VALIDATE'
  | 'FINALIZE';

export interface SolverProgressStepDef {
  step: SolverProgressStep;
  /** Percentage (0-100) the processor reports when entering this phase. */
  percent: number;
}

/** Canonical ordered list of solver phases with their reported percentages. */
export const SOLVER_PROGRESS_STEPS: readonly SolverProgressStepDef[] = [
  { step: 'LOAD_VERSION', percent: 10 },
  { step: 'COMPUTE_BLANKS', percent: 20 },
  { step: 'LOAD_DATA', percent: 30 },
  { step: 'CHECK_ELIGIBILITY', percent: 35 },
  { step: 'BUILD_REQUEST', percent: 40 },
  { step: 'SOLVE', percent: 50 },
  { step: 'VALIDATE', percent: 90 },
  { step: 'FINALIZE', percent: 100 },
];

/**
 * Progress payload emitted via `job.updateProgress`. Sent as an object (not a
 * bare number) so the phase label rides alongside the percentage; the jobs
 * gateway relays `step` in the WebSocket `message` field.
 */
export interface SolverProgressUpdate {
  percent: number;
  step: SolverProgressStep;
  rules?: SolverProgressRule[];
}

/**
 * Summary of what changed between before and after solver execution.
 */
export interface DeltaSummary {
  blankSlotsBefore: number;
  blankSlotsAfter: number;
  slotsFilled: number;
  unchangedAssignments: number;
}

/**
 * Metadata about the solver execution (engine-agnostic, versioned).
 */
export interface SolverMeta {
  durationMs: number;
  terminationReason?: string;
  engineId: string;
  contractVersion: string;
}

/**
 * Per-constraint contribution to the overall solver score.
 *
 * `name` is the constraint identifier as defined by the engine (e.g.
 * `maxCoverage`, `fairness`). `score` is the engine-formatted contribution
 * (e.g. `"0hard/-50medium/0soft"`). `matches` counts how many entities
 * triggered the constraint in the final solution. `weight` echoes the
 * configured weight for this run when the engine exposes it.
 */
export interface ConstraintBreakdownEntry {
  name: string;
  score: string;
  matches: number;
  weight?: number;
}

export interface SolverConstraintEvidence {
  rule: string;
  score: string;
}

/** Final engine decision and constraint matches for one solver slot. */
export interface SolverSlotOutcome {
  slotId: string;
  selectedEmployeeId?: string;
  eligibleEmployeeIds: string[];
  constraintEvidence: SolverConstraintEvidence[];
}

export interface SolverCandidateOutcome {
  employeeId: string;
  status: 'selected' | 'eligible_not_selected' | 'blocked';
  engineEvidence?: SolverConstraintEvidence[];
  reason?: SlotEligibilityReason;
  /** Resolved display name at solve time so UUIDs never reach the UI. */
  displayName?: string;
  /** Skill IDs this candidate is missing for the containing slot. Set only when `reason` is `SKILL_MISSING` or `SKILL_EXPIRED`. */
  missingSkillIds?: string[];
  /** Human-readable names matching `missingSkillIds` order. */
  missingSkillNames?: string[];
  /** For `eligible_not_selected` candidates: soft constraint that penalised them and the winner picked over them. */
  softLoss?: {
    constraint: SolverProgressRuleName;
    winnerEmployeeId: string;
    /** Optional numeric delta if the engine exposes it; UI shows the constraint label regardless. */
    scoreDelta?: number;
  };
}

/**
 * One still-blank slot after solving, with the reason tally for why no
 * candidate filled it. Feeds the result dialog's "why is this still blank"
 * per-slot explanation (as opposed to the instance-wide aggregate breakdown).
 */
export interface UnfilledSlotDetail {
  date: string;
  shiftUnitId: string;
  /** Parent shift's display name, e.g. "Schliessung" — empty if unavailable. */
  shiftName: string;
  /** How many seats of this shift/date still need a person. */
  stillUnfilled: number;
  /** Total seats this shift/date needs (shift capacity), so the UI can tell a structural shortage from an occasional one. */
  requiredCount?: number;
  /** Tally of why candidates were excluded, summed across the unfilled seats. */
  blockedByReason: Partial<Record<SlotEligibilityReason, number>>;
  /** Number of eligible candidates found for the unfilled seats. */
  eligibleCandidateCount?: number;
  /** Employee IDs that passed the hard eligibility filter for this slot. */
  eligibleCandidateIds?: string[];
  /** Per-employee hard-filter status merged with engine evidence when available. */
  candidateOutcomes?: SolverCandidateOutcome[];
  /** Skill names required by this shift, when no eligible candidate exists. */
  missingSkillNames?: string[];
  /** ISO local time, e.g. "17:00". Server-provided so the UI does not re-join to shiftUnits. */
  startTime?: string;
  /** ISO local time, e.g. "22:00". */
  endTime?: string;
  /** 0-6 (Mon=0). Redundant with `date` but avoids client-side re-derivation. */
  weekday?: number;
  /** Dominant reason from the final-schedule audit — drives the result dialog grouping. */
  dominantReason?: SlotEligibilityReason | 'ELIGIBLE_NOT_SELECTED';
  /**
   * Backend-computed reason a slot with `eligibleCandidateCount > 0` remained
   * unfilled after the solve (AC16). Absent when the slot has no eligible
   * candidates (its `blockedByReason` explains that case) or when the final
   * audit finds no hard blocker.
   *
   * - `CANDIDATES_BUSY_ELSEWHERE`: every eligible candidate is already
   *   assigned to another shift-unit on this date whose time overlaps this
   *   slot (and neither shift has `allowOverlap: true`). Filling this slot
   *   would double-book someone — the solver correctly declined.
   * - `CANDIDATES_NOT_SELECTED`: at least one eligible candidate is free at
   *   this slot's time in the final schedule. The final result left the
   *   candidate unassigned without a hard blocker the audit could prove.
   */
  unfilledReason?: 'CANDIDATES_BUSY_ELSEWHERE' | 'CANDIDATES_NOT_SELECTED';
}

/**
 * Result returned by the solver job processor.
 */
export interface SolverJobResult {
  planVersionId: string;
  mode: 'dry-run' | 'apply';
  /** Current plan-instance revision, when this is an instance solver result. */
  instanceUpdatedAt?: string;
  deltaSummary: DeltaSummary;
  diagnostics: DiagnosticCode[];
  proposedData: Record<string, Record<string, string[]>>;
  solverMeta: SolverMeta;
  /** Number of floater assignments rejected by service-layer validation during apply. */
  failedFloaterAssignments?: number;
  /**
   * Derivation of the proposal: how the hard eligibility filter narrowed the
   * workforce per blank slot. Explains why coverage stops short of 100% even with
   * all soft solver rules disabled. Absent on the pre-computed apply fast-path.
   */
  eligibilityBreakdown?: SlotEligibilityBreakdown;
  /** Engine-formatted final score, e.g. `"0hard/-50medium/-12soft"`. */
  score?: string;
  /** Per-constraint breakdown sorted by absolute contribution (largest first). Empty when the engine does not surface it. */
  breakdown?: ConstraintBreakdownEntry[];
  /** Per-slot detail for slots still blank after solving. Absent on the pre-computed apply fast-path. */
  unfilledSlotDetails?: UnfilledSlotDetail[];
}

/**
 * Data passed to the solver BullMQ job.
 */
export interface SolveBlanksJobData extends Record<string, unknown> {
  planVersionId: string;
  dryRun: boolean;
  options: SolverOptions;
  /** ISO timestamp of plan_versions.updatedAt for stale-data guard on apply */
  dataUpdatedAt?: string;
  /** Tenant ID for metrics attribution. Populated at job submission from the current
   * request's AsyncLocalStorage tenant context. Optional so existing queued jobs
   * without the field remain valid. */
  tenantId?: string;
  /** Tenant database URL for worker-side DB context restoration. */
  databaseUrl?: string;
  /**
   * Pre-computed solver result from a previous dry-run job.
   *
   * When present **and** `dryRun === false`, the processor skips the expensive
   * Timefold solve and writes this data directly (after the stale-data guard).
   * This turns the apply phase from ~10 s down to < 1 s.
   */
  proposedData?: Record<string, Record<string, string[]>>;
  /**
   * Floater assignments discovered in the proposed data (from a previous dry-run).
   * Used by the apply phase to auto-create claims without re-querying the solver.
   */
  floaterAssignments?: Array<{
    employeeId: string;
    offerId: string;
    /** ISO date (YYYY-MM-DD) — assignment-specific start, may be a sub-range of the offer window */
    startDate: string;
    /** ISO date (YYYY-MM-DD) — assignment-specific end */
    endDate: string;
    /** Linked request id, when the engine matched this assignment to an open request */
    requestId?: string;
  }>;
}

/**
 * Data passed to the conflict resolution BullMQ job.
 *
 * The processor loads the instance's stored conflicts, expands the current schedule,
 * removes conflicted employees from affected dates, builds solver blank slots,
 * calls the Timefold solver, and saves the result as assignment overrides.
 */
export interface ResolveConflictsJobData {
  planInstanceId: string;
  dryRun: boolean;
  options: SolverOptions;
  /** Inclusive date range the resolver may inspect or mutate. */
  startDate?: string;
  endDate?: string;
  /** ISO revision observed before the dry-run or apply was queued. */
  dataUpdatedAt?: string;
  /** Tenant ID for metrics attribution. */
  tenantId?: string;
  /** Tenant database URL for worker-side DB context restoration. */
  databaseUrl?: string;
  /**
   * Pre-computed resolution schedule from a previous dry-run job (date-keyed).
   * When present and dryRun === false, the processor writes this data directly
   * as assignment overrides, skipping the solve step.
   */
  proposedData?: Record<string, Record<string, string[]>>;
}

/**
 * Result returned by the conflict resolution job processor.
 */
/**
 * Outcome of a single conflicted assignment after a resolve-conflicts run.
 * One entry per removed (personId, date, shiftUnitId) slot.
 */
export interface ConflictResolutionEntry {
  personId: string;
  date: string;
  shiftUnitId: string;
  /** The conflict type that caused this assignment to be removed. */
  type: ConflictType;
  /** Human-readable label for the conflict (e.g. calendar event name, "Shift overlap"). */
  eventLabel: string;
  status: 'resolved' | 'unresolved';
  /** Employee who took over the slot, if the solver found a replacement. */
  replacementEmployeeId?: string;
  /** Only present when unresolved: tally of why candidates were excluded. */
  blockedByReason?: Partial<Record<SlotEligibilityReason, number>>;
  /** Only present when unresolved and SKILL_MISSING is a blocking reason. */
  missingSkillNames?: string[];
}

export interface ResolveConflictsJobResult {
  planInstanceId: string;
  /** 'dry-run' when nothing was persisted yet; 'apply' once written to the instance. */
  mode: 'dry-run' | 'apply';
  /** Current plan-instance revision returned by the preview/apply operation. */
  instanceUpdatedAt?: string;
  /** Number of calendar conflicts that were resolved (employees replaced) */
  conflictsResolved: number;
  /** Number of conflicts where no replacement could be found */
  conflictsUnresolved: number;
  /** Per-slot breakdown of how each conflict was resolved, for display in a result dialog. */
  resolutions: ConflictResolutionEntry[];
  /**
   * The resolved schedule (date-keyed), always returned regardless of mode.
   * The frontend holds onto this after a dry-run and resubmits it verbatim
   * (as `proposedData`, with `dryRun: false`) to apply without re-solving.
   */
  proposedData: Record<string, Record<string, string[]>>;
  diagnostics: DiagnosticCode[];
  solverMeta: SolverMeta;
  /** Engine-formatted final score from the last solver invocation. */
  score?: string;
  /** Per-constraint breakdown from the last solver invocation. */
  breakdown?: ConstraintBreakdownEntry[];
}

/**
 * Data passed to the instance solve-blanks BullMQ job.
 *
 * Unlike SolveBlanksJobData (which operates on plan version templates),
 * this job fills blank slots in a concrete plan instance schedule that
 * already has date-keyed overrides applied.
 */
export interface SolveInstanceBlanksJobData extends Record<string, unknown> {
  planInstanceId: string;
  planId: string;
  dryRun: boolean;
  options: SolverOptions;
  /** Tenant ID for metrics attribution. */
  tenantId?: string;
  /** Tenant database URL for worker-side DB context restoration. */
  databaseUrl?: string;
  /**
   * ISO 8601 timestamp of `plan_instances.updated_at` at dry-run time.
   * When set, the apply commit uses compare-and-swap on this value (AC14
   * in docs/specs/auto-fill-solver-correctness.md) so concurrent writes
   * cannot overwrite each other.
   */
  dataUpdatedAt?: string;
  /** Inclusive date range the solver may inspect or mutate. */
  startDate?: string;
  endDate?: string;
  /**
   * Pre-computed solver result from a previous dry-run job (date-keyed).
   * When present and dryRun === false, the processor writes this data
   * directly as __assignments__ overrides, skipping the solve step.
   */
  proposedData?: Record<string, Record<string, string[]>>;
}

const solverOptionsSchema = type({
  'maxCoverage?': type('boolean').configure({
    description: 'When true, the solver prioritises maximum shift coverage.',
    examples: [true],
  }),
  'fairness?': type('boolean').configure({
    description: 'When true, the solver balances workload fairly across employees.',
    examples: [true],
  }),
  'skillBalance?': type('boolean').configure({
    description: 'When true, the solver prefers employees whose skills match the shift requirements.',
    examples: [true],
  }),
  'balancePreference?': type('boolean').configure({
    description: 'When true, the solver prefers employees with lower cumulative hour balances (soft constraint).',
    examples: [true],
  }),
  'fast?': type('boolean').configure({
    description: 'When true, the solver stops much sooner after reaching a feasible solution, trading solution quality for speed.',
    examples: [false],
  }),
}).configure({ description: 'Toggles controlling which soft constraints the solver applies.' });

/**
 * ArkType schema for the POST .../resolve-conflicts request body.
 */
const baseInstanceUpdatedAtSchema = type('string').configure({
  description: 'ISO 8601 timestamp of the plan instance `updatedAt` observed by the client.',
  examples: ['2026-04-22T08:00:00.000Z'],
});

export const resolveConflictsRequestSchema = type({
  'dryRun?': type('boolean').configure({
    description: 'When true (the default), the resolution is computed and returned without persisting it; when false, it is applied.',
    examples: [true],
  }),
  'options?': solverOptionsSchema.configure({
    description: 'Optional solver option overrides for this run.',
  }),
  'baseInstanceUpdatedAt?': baseInstanceUpdatedAtSchema,
  'startDate?': type('string').configure({
    description: 'Inclusive first date the resolver may inspect or mutate.',
    examples: ['2026-08-01'],
  }),
  'endDate?': type('string').configure({
    description: 'Inclusive last date the resolver may inspect or mutate.',
    examples: ['2026-08-31'],
  }),
  'proposedData?': type('Record<string, Record<string, string[]>>').configure({
    description:
      'Pre-computed resolution schedule from a previous dry-run. When supplied together with `dryRun: false`, the apply phase skips solving and writes this data directly.',
  }),
}).configure({ description: 'Payload for triggering conflict resolution on a plan instance (dry-run or apply).' });

export type ResolveConflictsRequest = typeof resolveConflictsRequestSchema.t;

/**
 * Base version fingerprint for stale-data guard.
 */
const baseVersionSchema = type({
  'updatedAt?': type('string').configure({
    description: 'ISO 8601 timestamp of the plan version `updatedAt` used for optimistic concurrency.',
    examples: ['2026-04-22T08:00:00.000Z'],
  }),
}).configure({ description: 'Fingerprint of the base plan version used for stale-data detection.' });

/**
 * ArkType schema for the POST .../solve-blanks request body.
 */
export const solveBlanksRequestSchema = type({
  dryRun: type('boolean').configure({
    description: 'When true, the solver computes and returns a proposal without persisting it; when false, the proposal is applied.',
    examples: [true],
  }),
  'options?': solverOptionsSchema.configure({
    description: 'Optional solver option overrides for this run.',
  }),
  'baseVersion?': baseVersionSchema.configure({
    description: 'Optional base-version fingerprint that must match the current plan version, otherwise the request is rejected.',
  }),
  'proposedData?': type('Record<string, Record<string, string[]>>').configure({
    description:
      'Pre-computed solver result from a previous dry-run. When supplied together with `dryRun: false`, the apply phase skips solving and writes this data directly.',
  }),
}).configure({ description: 'Payload for solving blank slots on a plan version (dry-run or apply).' });

export type SolveBlanksRequest = typeof solveBlanksRequestSchema.t;

// (Previously a duplicate SolveInstanceBlanksJobData declaration appeared here; removed in 23-7 cleanup.)

/**
 * ArkType schema for the POST .../solve-blanks request body (instance-level solver).
 */
export const solveInstanceBlanksRequestSchema = type({
  dryRun: type('boolean').configure({
    description: 'When true, the solver computes and returns a proposal without persisting it.',
    examples: [true],
  }),
  'options?': solverOptionsSchema.configure({
    description: 'Optional solver option overrides for this run.',
  }),
  'baseInstanceUpdatedAt?': baseInstanceUpdatedAtSchema,
  'startDate?': type('string').configure({
    description: 'Inclusive first date the solver may inspect or mutate.',
    examples: ['2026-08-01'],
  }),
  'endDate?': type('string').configure({
    description: 'Inclusive last date the solver may inspect or mutate.',
    examples: ['2026-08-31'],
  }),
  'proposedData?': type('Record<string, Record<string, string[]>>').configure({
    description:
      'Pre-computed solver result from a previous dry-run. When supplied together with `dryRun: false`, the apply phase skips solving and writes this data directly.',
  }),
}).configure({ description: 'Payload for filling blank slots on a plan instance.' });

export type SolveInstanceBlanksRequest = typeof solveInstanceBlanksRequestSchema.t;

/**
 * Blank slot identified during blank-slot computation.
 */
export interface BlankSlot {
  weekday: number;
  /** Template day key (e.g. "weekday:1" or "w2:weekday:1") */
  dayKey?: string;
  shiftUnitId: string;
  shiftId: string;
  required: number;
  assigned: number;
  gap: number;
}

/**
 * An open labour-pool request forwarded to the solver so the engine can
 * match floater assignments to concrete coverage needs.
 * Engine enforcement (sub-range expansion, isSplittable) is a follow-up spec.
 */
export interface OpenLabourPoolRequest {
  requestId: string;
  startDate: string;
  endDate: string;
  headcount: number;
  requiredSkills: string[];
  isSplittable: boolean;
}

/**
 * A floater employee from the Labour Pool available for the solver to consider.
 * Carries a penalty weight so the solver prefers home-team employees first.
 */
export interface FloaterSolverEmployee {
  /** Planning employee ID in the system */
  employeeId: string;
  /** The Labour Pool offer this floater comes from */
  offerId: string;
  /** ISO dates (YYYY-MM-DD) on which this floater is available */
  availableDates: string[];
  /** Skill IDs the floater has (for skill-matching against shift requirements) */
  skillIds: string[];
  /**
   * Effective proficiency per skill, built via `buildEffectiveSkillRatings`.
   * Needed so floaters pass through the same hard eligibility pre-filter as
   * home-team employees — the engine only ever sees a skill *count*, so a
   * floater omitted from that filter would be assignable to any shift.
   */
  skillRatings: EffectiveSkillRatings;
  /**
   * Soft-constraint penalty weight applied to this employee's assignments.
   * Default 1.5 — the solver reduces the score proportionally to deprioritise
   * floaters relative to home-team employees.
   */
  penaltyWeight: number;
  /** Minimum claim duration in days; null for fixed-window offers */
  minDurationDays: number | null;
  /** Maximum claim duration in days; null for fixed-window offers */
  maxDurationDays: number | null;
}

/**
 * Request DTO sent to the Quarkus Timefold service.
 */
export interface SolverRequest {
  requestId: string;
  planId: string;
  planVersionId: string;
  currentData: Record<string, Record<string, string[]>>;
  slots: Array<{
    slotId: string;
    weekday: number;
    shiftUnitId: string;
    requiredCount: number;
    alreadyAssignedEmployeeIds: string[];
    allowedEmployeeIds: string[];
    incompatibleSlotIds: string[];
    /** Number of skills required by this shift unit — used for skill-match quality constraint. */
    requiredSkillCount: number;
    /** Duration of the shift in minutes — used for hour-weighted fairness constraint. */
    durationMinutes: number;
    /**
     * Which week (0-based) this slot belongs to, for combined multi-week solves
     * (SOLV-7). Lets the Java side disambiguate output as "w{N}:weekday:{D}"
     * instead of colliding same-weekday slots from different weeks into one
     * bucket. Omit for single-week solves — output stays the legacy bare
     * "weekday:{D}" format.
     */
    weekIndex?: number;
  }>;
  employees: Array<{
    id: string;
    skillIds: string[];
    /**
     * Team-mean-normalised overtime balance in hours (balance - teamMean).
     * Negative = owed hours relative to team, positive = over.
     */
    balanceHours: number;
    /** Number of skills this employee holds — used for skill-match quality constraint. */
    skillCount: number;
    /** Available minutes by 0-based solve week, net of baseline assignments. */
    weeklyMinutesBudgetByWeek: Record<string, number>;
  }>;
  shiftUnits: Array<{
    id: string;
    weekday: number;
    capacity: number;
    requiredSkillIds: string[];
    startTime: string;
    endTime: string;
  }>;
  options: SolverOptions;
  /**
   * Numeric soft-constraint weights. When provided, overrides the boolean
   * gates in `options` for the corresponding constraints. The TS-side helper
   * `solverOptionsToWeights` converts legacy boolean shapes to this form.
   */
  weights?: SolverWeights;
  /**
   * Optional floater employees from the Labour Pool in wire format.
   * Mapped from FloaterSolverEmployee domain model in buildSolverRequest.
   * Uses `id` (not `employeeId`) to match the Java FloaterEmployeeDto record.
   */
  floaterEmployees?: Array<{
    id: string;
    skillIds: string[];
    /** Team-mean-normalised balance hours. */
    balanceHours: number;
    skillCount: number;
    penaltyWeight: number;
    availableDates: string[];
    minDurationDays: number | null;
    maxDurationDays: number | null;
  }>;
  /**
   * Open labour-pool requests for the plan window.
   * Informational for the current engine; consumed once engine sub-range logic lands.
   */
  openRequests?: OpenLabourPoolRequest[];
}

/**
 * Raw metadata returned by an engine-specific HTTP response (before adapter enrichment).
 */
export interface RawSolverMeta {
  durationMs: number;
  terminationReason?: string;
}

/**
 * Response DTO returned by the Quarkus Timefold service (engine-specific, raw).
 */
export interface SolverResponse {
  requestId: string;
  score: string;
  proposedData: Record<string, Record<string, string[]>>;
  diagnostics: DiagnosticCode[];
  solverMeta: RawSolverMeta;
  /** Per-constraint contributions (omitted when the engine declines to compute them). */
  breakdown?: ConstraintBreakdownEntry[];
  slotOutcomes?: SolverSlotOutcome[];
}

// ─── Versioned Contracts (v1) ─────────────────────────────────────────────────

/**
 * Versioned request DTO for the solver engine adapter.
 * Engine-neutral — adapters map this to engine-specific payloads.
 */
export type SolverRequestV1 = SolverRequest;

/**
 * Versioned response DTO returned by solver engine adapters.
 * Engine-neutral — adapters normalize engine output into this shape.
 */
export interface SolverResponseV1 {
  proposedData: Record<string, Record<string, string[]>>;
  diagnostics: DiagnosticCode[];
  solverMeta: SolverMeta;
  /** Final engine-formatted score, e.g. `"0hard/-50medium/-12soft"`. */
  score?: string;
  /** Per-constraint contributions (omitted when the engine does not provide them). */
  breakdown?: ConstraintBreakdownEntry[];
  slotOutcomes?: SolverSlotOutcome[];
}

/**
 * Pluggable solver engine adapter interface.
 * Implement this to integrate a new solver engine.
 */
export interface SolverEngineAdapter {
  readonly engineId: string;
  readonly contractVersion: 'v1';

  healthCheck(): Promise<boolean>;
  solve(request: SolverRequestV1): Promise<SolverResponseV1>;
}

/**
 * Injection token for the active solver engine adapter.
 */
export const SOLVER_ENGINE_ADAPTER = 'SOLVER_ENGINE_ADAPTER';
